import React, {useEffect, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "../../AuthContext";
import {useNotifications} from "../../components/NotificationSystem";
import {AuthService} from "../../services/firebase/auth";

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const {signInWithEmail, signInWithGoogle, clearError, user, loading} =
    useAuth();
  const {addNotification} = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  // If user is already authenticated (e.g., due to persistence or external auth change),
  // redirect away from the login page.
  useEffect(() => {
    if (!loading && user) {
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");

      if (user.isEmailVerified === false) {
        navigate("/auth/email-verification", {replace: true});
      } else {
        navigate(redirect || "/overview", {replace: true});
      }
    }
  }, [user, loading, navigate, location.search]);

  // Helper function to check email verification and route accordingly
  const handlePostLoginNavigation = async () => {
    try {
      // Small delay to ensure Firebase auth state is fully settled
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get the current Firebase user directly (bypasses auth context delays)
      const firebaseUser = await AuthService.getCurrentUserWithVerification();

      if (firebaseUser && firebaseUser.emailVerified) {
        const params = new URLSearchParams(location.search);
        const redirect = params.get("redirect");
        if (redirect) {
          navigate(redirect);
        } else {
          navigate("/overview");
        }
      } else {
        // User is not verified - redirect to email verification page
        navigate("/auth/email-verification");
      }
    } catch (error) {
      // If verification check fails, assume unverified and redirect to verification
      // This is safer than defaulting to dashboard
      navigate("/auth/email-verification");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setIsLoading(true);

    try {
      await signInWithEmail(formData.email, formData.password);
      await handlePostLoginNavigation();
    } catch (error: any) {
      // Use notification system for error display
      addNotification({
        type: "error",
        title: "Sign In Failed",
        message:
          error.message ||
          "An error occurred during sign in. Please try again.",
        duration: 6000, // Show for 6 seconds
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    clearError();
    setIsLoading(true);

    try {
      await signInWithGoogle();
      await handlePostLoginNavigation();
    } catch (error: any) {
      // Use notification system for error display
      addNotification({
        type: "error",
        title: "Google Sign In Failed",
        message:
          error.message ||
          "An error occurred during Google sign in. Please try again.",
        duration: 6000, // Show for 6 seconds
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Sign In</h1>
        <p>Sign in to your League of Fabled Pursuits account</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <div className="google-button-container">
          <button
            type="button"
            className="btn btn-google"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            <svg className="google-icon" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? "Signing In..." : "Sign in with Google"}
          </button>
        </div>

        <p className="auth-link">
          Don't have an account? <Link to="/auth/register">Register here</Link>
        </p>

        <p className="auth-link">
          <Link to="/auth/forgot-password">Forgot your password?</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
